import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMyStudents } from '../../Api'
import { SeloRecuperacao } from '../../components/AvisoRecuperacao'
import { FiLogOut, FiChevronRight, FiUsers, FiAlertCircle } from 'react-icons/fi'

import {
    Page,
    Hero,
    HeroInner,
    HeroLeft,
    HeroAvatar,
    HeroText,
    HeroGreeting,
    HeroSubtitle,
    HeroCount,
    LogoutButton,
    Content,
    SectionHead,
    SectionLabel,
    SectionCount,
    StudentGrid,
    StudentCard,
    CardAccent,
    StudentAvatar,
    CardBody,
    StudentName,
    StudentMeta,
    CardFoot,
    StatusBadge,
    GoIcon,
    EmptyBox,
    EmptyIcon,
    SkeletonCard,
    SkeletonAvatar,
    SkeletonLine,
} from './style'

// iniciais com guarda de null (padrão do sistema)
const initials = (n) => (n || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0] || '')
    .join('')
    .toUpperCase()

const statusLabel = (s) => {
    switch ((s || '').toLowerCase()) {
        case 'ativo': return 'Ativo'
        case 'transferido': return 'Transferido'
        case 'inativo': return 'Inativo'
        case 'concluido': return 'Concluído'
        default: return s
    }
}

// nome da escola do aluno (id_school populado como objeto), com guarda de null
const schoolName = (student) => {
    const s = student?.id_school

    if (!s) return ''
    if (typeof s === 'object') return (s.name || '').trim()
    return ''
}

const ResponsibleStudents = () => {
    const navigate = useNavigate()
    const [students, setStudents] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const cancelled = useRef(false)

    const responsibleName = sessionStorage.getItem('name') || 'Responsável'

    useEffect(() => {
        cancelled.current = false

        const load = async () => {
            setLoading(true)
            setError('')
            try {
                const res = await getMyStudents()
                if (cancelled.current) return
                // exibe somente alunos ativos
                const all = res?.data?.data || []
                const onlyActive = all.filter(
                    s => (s.status || '').toLowerCase() === 'ativo'
                )
                setStudents(onlyActive)
            } catch (err) {
                if (cancelled.current) return
                setError('Não foi possível carregar os alunos. Tente novamente.')
                setStudents([])
            } finally {
                if (!cancelled.current) setLoading(false)
            }
        }

        load()
        return () => { cancelled.current = true }
    }, [])

    const handleOpenStudent = (student) => {
        // a página do aluno usa stdt-name no cabeçalho
        sessionStorage.setItem('stdt-name', student.name || '')
        // envia o id pelo state (useLocation); id na URL serve de fallback no F5
        navigate(`/responsible/student/${student._id}`, {
            state: { studentId: student._id }
        })
    }

    const handleLogout = () => {
        localStorage.removeItem('Id_responsible')
        localStorage.removeItem('token')
        sessionStorage.removeItem('token')
        localStorage.removeItem('type')
        sessionStorage.removeItem('name')
        sessionStorage.removeItem('cpf')
        navigate('/signin/responsible')
        window.location.reload()
    }

    const firstName = (responsibleName || '').trim().split(/\s+/)[0]
    const count = students.length
    const countLabel = count === 1 ? '1 dependente' : `${count} dependentes`

    return (
        <Page>
            <Hero>
                <HeroInner>
                    <HeroLeft>
                        <HeroAvatar>{initials(responsibleName)}</HeroAvatar>
                        <HeroText>
                            <HeroGreeting>Olá, {firstName} 👋</HeroGreeting>
                            <HeroSubtitle>
                                Acompanhe os dados escolares dos seus dependentes.
                            </HeroSubtitle>
                            {!loading && !error && count > 0 && (
                                <HeroCount>
                                    <FiUsers size={14} /> {countLabel}
                                </HeroCount>
                            )}
                        </HeroText>
                    </HeroLeft>

                    <LogoutButton onClick={handleLogout}>
                        <FiLogOut size={16} /> Sair
                    </LogoutButton>
                </HeroInner>
            </Hero>

            <Content>
                <SectionHead>
                    <SectionLabel>Meus dependentes</SectionLabel>
                    {!loading && !error && count > 0 && (
                        <SectionCount>{count}</SectionCount>
                    )}
                </SectionHead>

                {loading ? (
                    <StudentGrid>
                        {[1, 2, 3].map(i => (
                            <SkeletonCard key={i}>
                                <SkeletonAvatar />
                                <SkeletonLine $w="70%" $h="15px" />
                                <SkeletonLine $w="45%" />
                            </SkeletonCard>
                        ))}
                    </StudentGrid>
                ) : error ? (
                    <EmptyBox>
                        <EmptyIcon $tone="error"><FiAlertCircle size={26} /></EmptyIcon>
                        {error}
                    </EmptyBox>
                ) : students.length === 0 ? (
                    <EmptyBox>
                        <EmptyIcon><FiUsers size={26} /></EmptyIcon>
                        Nenhum aluno vinculado à sua conta.
                        <span>Procure a secretaria da escola para vincular seus dependentes.</span>
                    </EmptyBox>
                ) : (
                    <StudentGrid>
                        {students.map(student => (
                            <StudentCard
                                key={student._id}
                                onClick={() => handleOpenStudent(student)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        handleOpenStudent(student)
                                    }
                                }}
                            >
                                <CardAccent $status={student.status} />
                                <StudentAvatar>{initials(student.name)}</StudentAvatar>
                                <CardBody>
                                    <StudentName>{student.name}</StudentName>
                                    <SeloRecuperacao id_student={student._id} />
                                    <StudentMeta>
                                        {schoolName(student) || 'Escola não informada'}
                                    </StudentMeta>
                                    <CardFoot>
                                        {student.status && (
                                            <StatusBadge $status={student.status}>
                                                {statusLabel(student.status)}
                                            </StatusBadge>
                                        )}
                                        <GoIcon>
                                            Ver dados <FiChevronRight size={16} />
                                        </GoIcon>
                                    </CardFoot>
                                </CardBody>
                            </StudentCard>
                        ))}
                    </StudentGrid>
                )}
            </Content>
        </Page>
    )
}

export default ResponsibleStudents